import {
  pricesByCurrencies,
  SubscriptionType,
} from "@components/_subscription/helpers/constants"
import { Currency, PlansQuery } from "@generated"
import { useCurrency } from "@lib/hooks/use-currency"
import { usePlans } from "@lib/hooks/use-plans"
import { useMemo } from "react"

const formatPrice = (value: number) => value.toFixed(2)

export type SubscriptionDetailType = {
  initialCredit: number
  usps: string[]
  monthlyPrice: string
}

export type UseGetSubscriptionDetailsResult = {
  data: Record<SubscriptionType, SubscriptionDetailType>
  isLoading: boolean
  error?: unknown
  refetch: () => void
}

const getRawDetails = (currency: Currency) => ({
  Free: {
    initialCredit: 50,
    usps: [
      "AI Upscaling & AI Face Reconstruction",
      "AI Artifacts Removal",
      "Full API access",
      "Organise with folders",
      "PDF resolution up to 300 dpi",
    ],
    monthlyPrice: `${pricesByCurrencies[currency].symbol}${formatPrice(0)}`,
  },
  Basic: {
    initialCredit: 400,
    usps: [
      "All Try-out features",
      `Top-ups available: 200 credits for ${pricesByCurrencies[currency].symbol}${formatPrice(
        pricesByCurrencies[currency].prices.Basic.topUp,
      )}`,
      "Roll-over credits",
      "Cancel anytime",
      "PDF resolution up to 600 dpi",
    ],
    monthlyPrice: `${pricesByCurrencies[currency].symbol}${formatPrice(
      pricesByCurrencies[currency].prices.Basic.main,
    )}`,
  },
  Business: {
    initialCredit: 5000,
    usps: [
      "All Try-out features",
      `Auto top-up: 2.500 credits for ${pricesByCurrencies[currency].symbol}${formatPrice(
        pricesByCurrencies[currency].prices.Business.topUp,
      )}`,
      "Roll-over credits",
      "Fastest processing time",
      "PDF resolution up to 1200 dpi",
    ],
    monthlyPrice: `${pricesByCurrencies[currency].symbol}${formatPrice(
      pricesByCurrencies[currency].prices.Business.main,
    )}`,
  },
})

export const useGetSubscriptionDetails =
  (): UseGetSubscriptionDetailsResult => {
    const { currency } = useCurrency()
    const { data: plans, refetch } = usePlans()

    const { data, isLoading, error } = {
      data: plans,
      isLoading: false,
      error: undefined as unknown,
    }

    const merged = useMemo(() => {
      const rawDetails = getRawDetails(currency)
      if (!data || !data.length) {
        return rawDetails
      }

      const findPlanByNameIncludes = (needle: string) =>
        data.find((plan) => plan.name.toUpperCase().includes(needle)) ?? null

      const basicPlan = findPlanByNameIncludes("BASIC")
      const businessPlan = findPlanByNameIncludes("BUSINESS")

      const enrich = (
        type: SubscriptionType,
        plan: PlansQuery["plans"][number] | null,
      ): SubscriptionDetailType => {
        const base = rawDetails[type]
        if (!plan) {
          return base
        }

        const fromPlanInitialCredits = plan.monthlyCreditsAmount
        const fromPlanMonthlyPrice = plan.monthlyPrice
          ? `${pricesByCurrencies[currency].symbol}${formatPrice(plan.monthlyPrice)}`
          : null
        const fromPlanTopupPrice = plan.topUpPrice
          ? `${pricesByCurrencies[currency].symbol}${formatPrice(plan.topUpPrice)}`
          : null

        let updatedUsps = base.usps

        if (type === SubscriptionType.BASIC && fromPlanTopupPrice) {
          // Replace the Basic top-up USP line with one based on the remote price
          updatedUsps = base.usps.map((usp) => {
            if (!usp.startsWith("Top-ups available:")) return usp
            const credits = (plan.topupCreditsAmount || 400).toLocaleString(
              "nl-NL",
            )
            return `Top-ups available: ${credits} credits for ${fromPlanTopupPrice}`
          })
        }

        if (type === SubscriptionType.BUSINESS && fromPlanTopupPrice) {
          // Replace the Business auto top-up USP line with one based on the remote price
          updatedUsps = base.usps.map((usp) => {
            if (!usp.startsWith("Auto top-up:")) return usp
            const credits = (plan.topupCreditsAmount || 2500).toLocaleString(
              "nl-NL",
            )
            return `Auto top-up: ${credits} credits for ${fromPlanTopupPrice}`
          })
        }

        return {
          ...base,
          initialCredit: fromPlanInitialCredits || base.initialCredit,
          monthlyPrice: fromPlanMonthlyPrice || base.monthlyPrice,
          usps: updatedUsps,
        }
      }

      return {
        [SubscriptionType.FREE]: rawDetails[SubscriptionType.FREE],
        [SubscriptionType.BASIC]: enrich(SubscriptionType.BASIC, basicPlan),
        [SubscriptionType.BUSINESS]: enrich(
          SubscriptionType.BUSINESS,
          businessPlan,
        ),
      }
    }, [currency, data])

    return {
      data: merged,
      isLoading,
      error,
      refetch,
    }
  }
